import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  contentChild,
  effect,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';

import { Icon } from '../icon/icon';
import { IconName } from '../icon/icon-name';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/body-scroll-lock';
import { ModalFooterDirective } from './modal-footer.directive';
import { ModalHeaderActionDirective } from './modal-header-action.directive';
import { ModalKind, ModalSize } from './models/modal.model';

/** Card width per size preset. */
const WIDTH: Record<ModalSize, number> = { small: 420, medium: 560, large: 760, xlarge: 960 };

/** Focusable-element selector for the hand-rolled focus trap. */
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

let uid = 0;

/**
 * Universal modal shell. Owns the chrome (backdrop, card, header, footer) plus
 * dismissal (Escape / backdrop click / close X), body scroll-lock, and a minimal
 * focus trap — so consumers only project their body and pass a few inputs.
 *
 * Body is projected via <ng-content>. A custom footer can be supplied with
 * <ng-template modalFooter> (replaces the default Cancel/primary footer).
 *
 *   <app-modal [(open)]="open" [title]="'Add display'" primaryLabel="Add"
 *              (primaryAction)="submit()" (closed)="open.set(false)">
 *     <form>…</form>
 *   </app-modal>
 *
 * No @angular/cdk — the focus trap is a best-effort Tab-wrap, not WCAG-complete.
 */
@Component({
  selector: 'app-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslocoDirective, NgTemplateOutlet],
  templateUrl: './modal.html',
  styleUrl: './modal.css',
  host: {
    '(document:keydown.escape)': 'onEscape()',
    '(document:keydown.tab)': 'onTab($event)',
  },
})
export class Modal {
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  // ── Visibility (two-way) ─────────────────────────────────────────────
  readonly open = model.required<boolean>();

  // ── Chrome ───────────────────────────────────────────────────────────
  readonly title = input.required<string>();
  readonly subtitle = input('');
  readonly kind = input<ModalKind>('default');
  /** Optional header icon (used when kind is 'default'). */
  readonly icon = input<IconName | null>(null);
  readonly size = input<ModalSize>('medium');
  /** Card min-height as min(minHeightPx, 90vh) — the card still grows with
   *  content up to the 90vh cap, so it only scrolls when the viewport is
   *  genuinely too small. null = fully content-driven (default). */
  readonly minHeightPx = input<number | null>(null);
  /** Card height as min(heightPx, 90vh) — unlike `minHeightPx`, this is a
   *  fixed height, not just a floor: the card never grows past it, so a
   *  consumer whose body content changes size (e.g. a wizard step showing
   *  an inline panel) scrolls internally instead of resizing the card.
   *  Takes precedence over `minHeightPx` when both are set. null = fully
   *  content-driven (default, existing behaviour unaffected). */
  readonly heightPx = input<number | null>(null);
  /** Remove the body's padding + scrolling so consumers own the full body layout
   *  (e.g. a wizard whose step rail must span header-to-footer). */
  readonly flushBody = input(false);

  // ── Footer (config-driven default) ───────────────────────────────────
  readonly primaryLabel = input<string | null>(null);
  readonly primaryDisabled = input(false);
  readonly primaryDestructive = input(false);
  readonly showCancel = input(true);
  readonly cancelLabel = input<string | null>(null);

  // ── Behaviour ────────────────────────────────────────────────────────
  readonly dismissable = input(true);
  /** When true, Escape/backdrop/X emit `closeRequested` instead of closing —
   *  the consumer decides (e.g. confirm discarding dirty wizard data). */
  readonly interceptClose = input(false);

  // ── Outputs ──────────────────────────────────────────────────────────
  readonly closed = output<void>();
  readonly primaryAction = output<void>();
  readonly closeRequested = output<void>();

  // ── Projected footer / header action ─────────────────────────────────
  readonly footerTpl = contentChild(ModalFooterDirective);
  readonly headerActionTpl = contentChild(ModalHeaderActionDirective);

  /** Per-instance id for aria-labelledby. */
  protected readonly titleId = `modal-title-${uid++}`;

  protected readonly widthPx = computed(() => WIDTH[this.size()]);

  /** Whether the default (config) footer has anything to show. */
  protected readonly hasConfigFooter = computed(
    () => this.primaryLabel() !== null || this.showCancel(),
  );

  /** Element focused before the modal opened, to restore on close. */
  private previouslyFocused: HTMLElement | null = null;
  private locked = false;

  constructor() {
    // Lock/unlock body scroll + manage focus as `open` toggles. lock() schedules
    // focus-into-card on every open (via queueMicrotask, once the card's DOM
    // exists) — so there is a single, per-open focus path here, not a one-shot.
    effect(() => {
      if (this.open()) this.lock();
      else this.unlock();
    });

    this.destroyRef.onDestroy(() => this.unlock());
  }

  // ── Scroll-lock (ref-counted, shared with other overlays) ─────────────

  private lock(): void {
    if (this.locked) return;
    this.locked = true;
    this.previouslyFocused = (document.activeElement as HTMLElement) ?? null;
    lockBodyScroll();
    // The card renders this tick; focus after it exists.
    queueMicrotask(() => this.focusFirst());
  }

  private unlock(): void {
    if (!this.locked) return;
    this.locked = false;
    unlockBodyScroll();
    this.previouslyFocused?.focus?.();
    this.previouslyFocused = null;
  }

  // ── Focus management ─────────────────────────────────────────────────

  private card(): HTMLElement | null {
    return this.hostEl.nativeElement.querySelector('[data-modal-card]');
  }

  private focusables(): HTMLElement[] {
    const card = this.card();
    return card ? Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
  }

  private focusFirst(): void {
    if (!this.open()) return;
    const items = this.focusables();
    (items[0] ?? this.card())?.focus?.();
  }

  /** Best-effort Tab-wrap trap (no CDK). */
  protected onTab(event: Event): void {
    if (!this.open() || !(event instanceof KeyboardEvent)) return;
    const items = this.focusables();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement;
    if (event.shiftKey && active === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && active === last) {
      first.focus();
      event.preventDefault();
    }
  }

  // ── Dismissal ────────────────────────────────────────────────────────

  /** Single dismissal path: close + notify (or defer to the consumer). */
  protected dismiss(): void {
    if (this.interceptClose()) {
      this.closeRequested.emit();
      return;
    }
    this.open.set(false);
    this.closed.emit();
  }

  protected onEscape(): void {
    if (this.open() && this.dismissable()) this.dismiss();
  }

  protected onBackdropMouseDown(): void {
    if (this.dismissable()) this.dismiss();
  }

  protected onPrimary(): void {
    this.primaryAction.emit();
  }
}
